import { useQuery } from '@tanstack/react-query';
import { Radio, Select, Switch } from 'antd';
import type { Chart, ChartOptions } from 'chart.js';
import { useMemo, useRef, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import ConfigIcon from '@/assets/icons/SettingIcon';
import { CenterFlex, Flex, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import Spinner from '@/components/Common/Spinner';
import Transition from '@/components/Common/Transition';
import TransitionPortal from '@/components/Common/TransitionPortal';
import TooltipV2 from '@/components/Tooltip/TooltipV2';
import Typo from '@/components/Typography/Typo';
import AMP_KEYS from '@/configs/amp-track-keys';
import queryKeys from '@/configs/react-query-keys';
import useExplorerColor from '@/features/InsightExplorer/hooks/useExplorerColor';
import useExplorerPaylaod from '@/features/InsightExplorer/hooks/useExplorerPaylaod';
import {
  InsightExplorerByType,
  type InsightExplorerFieldType,
  InsightExplorerTrendViewType,
  InsightExplorerTrendViewTypeMap,
  InsightExplorerViewType,
} from '@/features/InsightExplorer/types/constants';
import type { InsightExplorerTrendResponse } from '@/features/InsightExplorer/types/types';
import { insightExplorerUtils } from '@/features/InsightExplorer/utils/insight-explorer.utils';
import useColor from '@/hooks/domain/useColor';
import useDropdownPos from '@/hooks/pure/useDropdownPos';
import useModalState from '@/hooks/pure/useModalState';
import insightExplorerService from '@/services/insight-explorer';
import useInsightExplorerStore from '@/stores/InsightExplorerStore';
import { getFormatByRangeUnit, getTimezoneDayjs } from '@/utils/time';
import { track } from '@/utils/track/track';

import type { HierarchyNodeData } from '../../../InsightExplorerSidebar/HierarchyNavigator/HierarchyNavigator';
import ExplorerChartCustomTooltip from '../../ExplorerChartCustomTooltip/ExplorerChartCustomTooltip';
import { useExplorerChartTooltip } from '../../ExplorerChartCustomTooltip/useExplorerChartTooltip';

interface Props {
  distributionTargets: string[];
  data?: InsightExplorerTrendResponse;
}

const VolumeTrendBySubtopicsSection = (props: Props) => {
  const { data } = props;
  const { field_type } = useParams();
  const controlPanelTriggerEl = useRef<HTMLButtonElement>(null);
  const chartRef = useRef<Chart<'line'>>(null);
  const { distributionTargets } = props;
  const { selectedNode, by, view, dateState, findNode } =
    useInsightExplorerStore();
  const getLabelColor = useExplorerColor(
    field_type as InsightExplorerFieldType,
  );
  const { filterPayload } = useExplorerPaylaod();

  if (!selectedNode) throw new Error('selectedNode is required');
  const colors = useColor();
  const [selectedFilter, setSelectedFilter] = useState<number>(5);

  const [showTotal, setShowTotal] = useState(false);
  const [trendViewType, setTrendViewType] =
    useState<InsightExplorerTrendViewType>(
      InsightExplorerTrendViewType.NEGATIVE_VOLUME,
    );

  const { data: childDistData, isLoading: isChildDistDataLoading } = useQuery({
    queryKey: [
      queryKeys.insightExplorer.INSIGHT_EXPLORER_CHILDREN,
      by,
      selectedNode,
      view,
      filterPayload,
      dateState.rangeUnit,
      selectedFilter,
      distributionTargets,
    ],
    queryFn: () =>
      insightExplorerService.getInsightExplorerTrend({
        // taxonomy 타입일 경우 taxonomy_topic으로 변경 (taxonomy의 하위는 항상 taxonomy_topic임)
        by:
          by === InsightExplorerByType.TAXONOMY
            ? InsightExplorerByType.TAXONOMY_TOPIC
            : by,
        target_id: distributionTargets.slice(0, selectedFilter),
        view: view,
        filter: filterPayload,
        view_type: dateState.rangeUnit,
      }),
  });

  const { tooltipData, chartTooltip, hideTooltip } = useExplorerChartTooltip({
    chart: chartRef.current,
  });

  const chartOptions: ChartOptions<'line'> = useMemo(
    () => ({
      events: ['mouseleave', 'mousemove', 'click'],
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        eventPlugin: {
          events: ['mouseleave', 'click'],
        },
        datalabels: {
          display: false,
        },
        legend: {
          display: true,
          position: 'right',
        },
        tooltip: {
          enabled: false,
          external: chartTooltip,
        },
      },
      scales: {
        x: {
          display: true,
          grid: {
            color: colors.gray250,
            drawTicks: false,
          },
          ticks: {
            color: colors.gray500,
            font: {
              size: 11,
              family: 'Instrument Sans',
            },
            maxRotation: 45,
          },
        },
        y: {
          display: true,
          grid: {
            color: colors.gray250,
            drawTicks: false,
          },
          ticks: {
            color: colors.gray500,
            font: {
              size: 11,
              family: 'Instrument Sans',
            },
          },
          beginAtZero: true,
        },
      },
      elements: {
        line: {
          borderWidth: 2,
        },
        point: {
          hoverRadius: 6,
        },
      },
    }),
    [chartTooltip, colors.gray250, colors.gray500],
  );

  const chartData = useMemo(() => {
    if (!childDistData)
      return {
        labels: [],
        datasets: [],
      };

    const labels = childDistData?.[0]?.volume.buckets.map((bucket) => {
      const date = bucket.bucket.toString();
      const formatted = getFormatByRangeUnit(dateState.rangeUnit)(
        getTimezoneDayjs(date),
      );

      return formatted;
    });

    const datasets = childDistData?.map((item, index) => {
      let chartData: number[];

      if (
        view === InsightExplorerViewType.SENTIMENTS &&
        trendViewType === InsightExplorerTrendViewType.SENTIMENT_SCORE
      ) {
        // SCORE 모드일 때 sentiment score 계산
        chartData = item.volume.buckets.map((bucket, bucketIndex) => {
          const totalVolume = bucket.count;
          const negativeVolume =
            item.negative_volume.buckets[bucketIndex]?.count || 0;
          return insightExplorerUtils.calcSentimentScore(
            totalVolume,
            negativeVolume,
          );
        });
      } else if (
        view === InsightExplorerViewType.SENTIMENTS &&
        trendViewType === InsightExplorerTrendViewType.NEGATIVE_VOLUME
      ) {
        // VOLUME 모드일 때 negative volume 사용
        chartData = item.negative_volume.buckets.map((bucket) => bucket.count);
      } else {
        // 기본적으로 total volume 사용
        chartData = item.volume.buckets.map((bucket) => bucket.count);
      }

      return {
        alias: item.alias,
        label: findNode(item.alias)?.name || item.alias,
        order: index + 1,
        data: chartData,
        borderColor: getLabelColor(item.alias),
        backgroundColor: getLabelColor(item.alias),
        borderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
        tension: 0,
      };
    });

    // showTotal이 true일 때 total dataset 추가
    if (showTotal && childDistData && childDistData.length > 0) {
      let result: number[] = [];
      const totalData = data?.[0];
      if (!totalData)
        return {
          labels,
          datasets,
        };

      if (
        view === InsightExplorerViewType.SENTIMENTS &&
        trendViewType === InsightExplorerTrendViewType.SENTIMENT_SCORE
      ) {
        // SCORE 모드일 때 모든 아이템의 sentiment score의 평균 계산
        result = totalData.volume.buckets.map((bucket, bucketIndex) => {
          const totalVolumeSum = bucket.count;
          const negativeVolumeSum =
            totalData.negative_volume.buckets[bucketIndex]?.count || 0;
          return insightExplorerUtils.calcSentimentScore(
            totalVolumeSum,
            negativeVolumeSum,
          );
        });
      } else if (
        view === InsightExplorerViewType.SENTIMENTS &&
        trendViewType === InsightExplorerTrendViewType.NEGATIVE_VOLUME
      ) {
        // VOLUME 모드일 때 negative volume의 합계
        result = totalData.negative_volume.buckets.map((bucket) => {
          return bucket.count;
        });
      } else {
        // 기본적으로 total volume의 합계
        result = totalData.volume.buckets.map((bucket) => {
          return bucket.count;
        });
      }

      const totalDataset = {
        alias: selectedNode.id,
        label: 'Total',
        data: result,
        order: datasets.length + 1,
        borderColor:
          view === InsightExplorerViewType.SENTIMENTS
            ? colors.red(0.7)
            : colors.primaryAlpha(0.7),
        backgroundColor:
          view === InsightExplorerViewType.SENTIMENTS
            ? colors.red(0.1)
            : colors.primaryAlpha(0.1),
        borderWidth: 1,
        pointRadius: 2,
        pointHoverRadius: 2,
        tension: 0,
        fill: true, // area chart로 만들기 위한 설정
      };

      datasets.unshift(totalDataset); // total을 맨 앞에 추가
    }

    return {
      labels,
      datasets,
    };
  }, [
    childDistData,
    showTotal,
    dateState.rangeUnit,
    view,
    trendViewType,
    findNode,
    getLabelColor,
    data,
    selectedNode.id,
    colors,
  ]);

  const {
    status: controlPanelStatus,
    open: openControlPanel,
    close: closeControlPanel,
  } = useModalState();

  return (
    <Transition
      status={!isChildDistDataLoading}
      fallback={
        <CenterFlex style={{ height: 384 }}>
          <Spinner />
        </CenterFlex>
      }
    >
      <VFlex g={16}>
        <Flex j="space-between" a="center" style={{ height: 32 }}>
          <Flex a="center" g={16}>
            <Button
              ref={controlPanelTriggerEl}
              variant="secondary"
              sizeType="m"
              rounded
              leftIcon={<ConfigIcon />}
              onClick={() => openControlPanel()}
            >
              View Top {selectedFilter} {showTotal ? 'with Total' : ''}
            </Button>
            <ControlPanel
              status={controlPanelStatus}
              close={closeControlPanel}
              showTotal={showTotal}
              setShowTotal={setShowTotal}
              selectedFilter={selectedFilter}
              setSelectedFilter={setSelectedFilter}
              triggerEl={controlPanelTriggerEl.current}
              selectedNode={selectedNode}
            />
          </Flex>

          <Flex a="center" g={16}>
            {view === InsightExplorerViewType.SENTIMENTS && (
              <Radio.Group value={trendViewType}>
                {InsightExplorerTrendViewTypeMap.toArray().map((op) => {
                  return (
                    <TooltipV2
                      key={op.value}
                      tooltipNode={
                        op.value ===
                        InsightExplorerTrendViewType.SENTIMENT_SCORE ? (
                          <>
                            A higher score indicates a larger share of positive
                            feedback.
                          </>
                        ) : null
                      }
                    >
                      <Radio.Button
                        value={op.value}
                        onChange={() => {
                          setTrendViewType(op.value);
                          track(AMP_KEYS.INSIGHT_EXPLORER_TREND_VIEW_SELECT, {
                            trend_view: op.label,
                          });
                        }}
                      >
                        {op.label}
                      </Radio.Button>
                    </TooltipV2>
                  );
                })}
              </Radio.Group>
            )}
          </Flex>
        </Flex>
        <MultiLineChartWrapper onMouseLeave={hideTooltip}>
          <Line
            key={JSON.stringify(filterPayload)}
            data={chartData}
            options={chartOptions}
            ref={chartRef}
          />
          {tooltipData && (
            <ExplorerChartCustomTooltip
              tooltip={tooltipData}
              from="subtopic"
              meta={{
                field: field_type as InsightExplorerFieldType,
                selectedNode: selectedNode,
                view: view,
                viewType: dateState.rangeUnit,
                filterPayload: filterPayload,
                findNode: findNode,
                trendViewType: trendViewType,
              }}
              hideTooltip={hideTooltip}
            />
          )}
        </MultiLineChartWrapper>
      </VFlex>
    </Transition>
  );
};

const MultiLineChartWrapper = styled.div`
  position: relative;
  height: 384px;
  width: 100%;
`;

type PanelProps = {
  status: boolean;
  showTotal: boolean;
  selectedFilter: number;
  triggerEl: HTMLElement | null;
  selectedNode?: HierarchyNodeData;
  close: () => void;
  setShowTotal: (value: boolean) => void;
  setSelectedFilter: (value: number) => void;
};

const ControlPanel = (props: PanelProps) => {
  const {
    status,
    close,
    showTotal,
    setShowTotal,
    selectedFilter,
    setSelectedFilter,
    triggerEl,
    selectedNode,
  } = props;

  const { dropdownPos, setDropdownRef } = useDropdownPos(props.status, {
    el: triggerEl,
    options: {
      offset: {
        y: 8,
      },
    },
  });

  const filterOptions = [
    { label: 'Top5', value: 5 },
    { label: 'Top10', value: 10 },
  ];

  return (
    <TransitionPortal
      layerId="dropdown-layer"
      status={status}
      close={close}
      style={{
        ...dropdownPos,
        padding: 20,
        width: 260,
        borderRadius: 8,
      }}
      background={{
        enable: true,
      }}
    >
      <VFlex g={16} ref={setDropdownRef}>
        <Typo type="label2" weight="sb">
          Setting
        </Typo>
        <VFlex g={8}>
          <Flex j="space-between" a="center">
            <Typo type="label3" weight="m">
              Show
            </Typo>
            <Select
              value={selectedFilter}
              onChange={(value) => {
                setSelectedFilter(value);
                track(AMP_KEYS.INSIGHT_EXPLORER_TOP_SELECT, {
                  top: value,
                });
              }}
              options={filterOptions}
              getPopupContainer={(el) => el.parentElement!}
            />
          </Flex>
          {!selectedNode?.exceptSelf && (
            <Flex j="space-between" a="center">
              <Typo type="label3" weight="m">
                Include overall
              </Typo>
              <Switch
                checked={showTotal}
                onChange={(value) => {
                  setShowTotal(value);
                  track(AMP_KEYS.INSIGHT_EXPLORER_INCLUDE_OVERALL_TOGGLE, {
                    include_overall: value ? 'true' : 'false',
                  });
                }}
              />
            </Flex>
          )}
        </VFlex>
      </VFlex>
    </TransitionPortal>
  );
};

export default VolumeTrendBySubtopicsSection;
